"use client";

import React, { useEffect, useState } from "react";
import { ChevronRight, FileCheck, Sparkles, FastForward, Moon, ShieldCheck } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Link } from "@/components/common/Link";
import { scrollToElement } from "@/lib/scroll";
interface TermItem {
  id: string;
  badge: string;
  title: string;
  text: string;
  code: string;
  icon?: React.ComponentType<{
    className?: string;
  }>;
}
const TERMS_DATA: TermItem[] = [{
  id: "educational-purpose",
  badge: "01 / SCOPE & ACCURACY",
  code: "TERMS-010",
  title: "Educational Purpose & Limitation of Models",
  text: "All interactive simulators, What-If parameter tools, speculative thought experiments, and quantum concept instruments are provided strictly for educational and conceptual illustration. Simplified physical models do not substitute for formal mathematical derivations or engineering calculations."
}, {
  id: "intellectual-property",
  badge: "02 / RIGHTS & ARCHIVES",
  code: "TERMS-020",
  title: "Intellectual Property & Citations",
  text: "Original editorial text, visual designs, and interactive simulator code are protected under intellectual property statutes. Primary research citations, astronomical telescope imagery from public observatories (NASA, ESA, ESO), and mathematical equations belong to their respective researchers and public archives."
}, {
  id: "appropriate-usage",
  badge: "03 / ACCESS PROTOCOL",
  code: "TERMS-030",
  title: "Appropriate Usage & Free Access",
  text: "All exploratory tools, generators, and digital dossiers on Quantum Liaison are made available completely free of charge. Visitors are granted a revocable, non-exclusive license to use the platform for personal, scholastic, and non-commercial educational purposes. Automated scraping, malicious intrusion, or unauthorized extraction of the simulation database is strictly prohibited.",
  icon: FileCheck
}, {
  id: "infinite-self-dossier",
  badge: "04 / DIGITAL EXPERIENCES",
  code: "TERMS-040",
  title: "The Multiverse Life Dossier",
  text: "The Multiverse Life Dossier is a free digital product generated directly in the browser and downloadable as a PDF document. One generated report presents four fictional parallel lives constructed from combinatorial narrative models. The content is imaginative, science-inspired multiverse fiction—never scientific, psychological, or predictive advice, and not a factual claim that parallel universes exist.",
  icon: Sparkles
}, {
  id: "future-self-dossier",
  badge: "05 / DIGITAL EXPERIENCES",
  code: "TERMS-050",
  title: "Your Future Self — The Time Travel Dossier",
  text: "Your Future Self is a free reflective digital experience and downloadable PDF report exploring hypothetical future trajectories. All narratives represent speculative foresight scenarios rather than deterministic predictions, psychological evaluations, or professional advice.",
  icon: FastForward
}, {
  id: "cosmic-destiny-code",
  badge: "06 / DIGITAL EXPERIENCES",
  code: "TERMS-060",
  title: "The Cosmic Destiny Code",
  text: "The Cosmic Destiny Code is a free interpretive astrological report generated in your browser and available for immediate PDF download. It is offered solely for personal reflection, philosophical amusement, and entertainment, with no claims of astrological certainty, fate, or predictive power.",
  icon: Moon
}, {
  id: "digital-immortality",
  badge: "07 / DIGITAL EXPERIENCES",
  code: "TERMS-070",
  title: "Digital Immortality",
  text: "Digital Immortality provides a private encrypted personal archive. Access requires a free account; records are encrypted directly on your device before transfer, and the site never retains your vault passphrase. The feature is an interactive philosophical archive and makes no scientific claim of consciousness transfer, cybernetic continuation, or literal immortality.",
  icon: ShieldCheck
}];
export default function Terms() {
  const [activeId, setActiveId] = useState<string>(TERMS_DATA[0].id);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 160;
      for (let i = TERMS_DATA.length - 1; i >= 0; i--) {
        const el = document.getElementById(TERMS_DATA[i].id);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveId(TERMS_DATA[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="terms" data-nav="dark" className="relative text-foreground py-12 md:py-20 lg:py-24 w-full max-w-full">
      <div className="relative mx-auto max-w-6xl w-full px-3.5 sm:px-6 lg:px-8">
        {/* Compact Hero with SectionHeading */}
        <SectionHeading as="h1" eyebrow="PLATFORM GOVERNANCE • REVISION 2.4.0" title="Terms of Service" description="By navigating the Quantum Liaison platform, you agree to access our educational instruments, scientific visualizations, and articles under the following terms." align="center" className="mb-8 sm:mb-16" />

        {/* Monolith layout with sticky desktop Table of Contents */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-start w-full">
          {/* Sticky Table of Contents Sidebar */}
          <aside className="w-full min-w-0 lg:col-span-4 lg:sticky lg:top-28">
            <div className="ql-glass p-4 sm:p-6 min-w-0 break-words">
              <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-3 sm:mb-4">
                <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-2">
                  <span className="size-1.5 rotate-45 bg-primary" aria-hidden="true" />
                  Table of Contents
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">{TERMS_DATA.length} Articles</span>
              </div>

              {/* Mobile Quick-Jump Chips */}
              <nav aria-label="Terms articles (mobile)" className="flex lg:hidden flex-wrap gap-2 mb-2 w-full">
                {TERMS_DATA.map((term, idx) => {
                const isActive = activeId === term.id;
                return <Link key={term.id} to={`#${term.id}`} onClick={e => {
                  e.preventDefault();
                  scrollToElement(term.id);
                }} className={`ql-chip min-h-[44px] ${isActive ? "data-[active=true]" : ""}`} data-active={isActive ? "true" : "false"}>
                      <span className="text-[10px] opacity-75">0{idx + 1}</span>
                      <span className="break-words">{term.title}</span>
                    </Link>;
              })}
              </nav>

              {/* Desktop links list */}
              <nav aria-label="Terms articles (desktop)" className="hidden lg:flex flex-col gap-2">
                {TERMS_DATA.map((term, idx) => {
                const isActive = activeId === term.id;
                return <Link key={term.id} to={`#${term.id}`} onClick={e => {
                  e.preventDefault();
                  scrollToElement(term.id);
                }} className={`group flex items-center justify-between p-3 rounded-lg border text-left transition-all ${isActive ? "border-primary/60 bg-primary/10 text-foreground shadow-[0_0_15px_-3px_var(--glow-primary)]" : "border-transparent hover:border-border hover:bg-card/60 text-muted-foreground hover:text-foreground"}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-mono text-xs px-2 py-0.5 rounded border transition-colors ${isActive ? "border-primary/60 bg-primary/20 text-primary font-semibold" : "border-border/60 bg-muted/40 text-muted-foreground group-hover:text-foreground"}`}>
                          0{idx + 1}
                        </span>
                        <span className="text-sm font-medium truncate font-sans">
                          {term.title}
                        </span>
                      </div>
                      <ChevronRight className={`size-4 shrink-0 transition-transform ${isActive ? "text-primary translate-x-0.5" : "text-muted-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5"}`} />
                    </Link>;
              })}
              </nav>

              {/* Side metadata telemetry */}
              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-border/50 text-[11px] font-mono text-muted-foreground flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span>FRAMEWORK</span>
                  <span className="text-foreground">ACADEMIC & SCHOLASTIC</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>ENFORCEMENT</span>
                  <span className="text-primary font-semibold">ACTIVE</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Reading Column: ql-glass document panel (~72ch readable width) */}
          <div className="w-full min-w-0 lg:col-span-8 flex flex-col gap-6">
            <div className="ql-glass p-5 sm:p-10 md:p-12 min-w-0 break-words">
              {/* Document metadata banner with preserved exact revision citation */}
              <div className="flex flex-wrap items-center justify-between gap-3 pb-5 sm:pb-6 border-b border-border/60 mb-6 sm:mb-8 font-mono text-xs">
                <div className="flex items-center gap-2 text-primary font-medium">
                  <span className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
                  <span className="break-words">QUANTUM LIAISON REPOSITORY</span>
                </div>
                <span className="text-muted-foreground break-words">REVISION 2.4.0 • ALL EDUCATIONAL POLICIES ACTIVE</span>
              </div>

              {/* Terms Articles List */}
              <div className="flex flex-col gap-6 sm:gap-8">
                {TERMS_DATA.map((item, index) => {
                const IconComponent = item.icon || FileCheck;
                const isActive = activeId === item.id;
                return <article key={item.id} id={item.id} className={`scroll-mt-28 transition-colors p-4 sm:p-6 rounded-xl border min-w-0 break-words ${isActive ? "border-primary/40 bg-card/80 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.5)]" : "border-border/60 bg-card/40"}`}>
                      <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10 text-primary shadow-[0_0_12px_-2px_var(--glow-primary)]">
                            <IconComponent className="size-4 sm:size-5" />
                          </div>
                          <div className="min-w-0">
                            <span className="block font-mono text-[11px] font-semibold text-primary tracking-widest uppercase">
                              {item.badge}
                            </span>
                            <h2 className="font-serif text-base sm:text-xl font-bold text-foreground tracking-tight break-words">
                              {index + 1}. {item.title}
                            </h2>
                          </div>
                        </div>

                        <span className="font-mono text-[10px] sm:text-[11px] px-2 py-0.5 rounded border border-border/60 bg-muted/40 text-muted-foreground self-start sm:self-auto shrink-0">
                          {item.code}
                        </span>
                      </div>

                      <div className="text-xs sm:text-base text-muted-foreground leading-relaxed pl-0 sm:pl-13 break-words overflow-x-auto">
                        <p className="break-words">{item.text}</p>
                      </div>
                    </article>;
              })}
              </div>

              {/* Exact preserved citation tag */}
              <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-border/60 text-xs font-mono text-muted-foreground/80 tracking-wider text-center sm:text-left break-words">
                QUANTUM LIAISON REPOSITORY • REVISION 2.4.0 • ALL EDUCATIONAL POLICIES ACTIVE
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}